import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import "./RecentlyViewed.css";
import Loader from "./Loader";
import AuthModal from "./AuthModal";

const API_BASE = "https://api.mariamlyluxe.com/public";
const BASE = "https://api.mariamlyluxe.com/public";
const PLACEHOLDER = null;

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

function money(n = 0) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(2)}`;
  }
}

function fileUrl(path = "") {
  const clean = String(path || "").replace(/^\/+/, "");
  if (!clean) return "";
  if (clean.startsWith("http")) return clean;
  if (clean.startsWith("storage/")) return `${BASE}/${clean}`;
  return `${BASE}/storage/${clean}`;
}

function CartIcon({ size = 18, strokeWidth = 1.8, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <line x1="10" y1="6" x2="14" y2="6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path
        d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h9"
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function RecentlyViewed() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const loadRecentlyViewed = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      setProducts([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/products/recently-viewed`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to load recently viewed products");
      }

      // الـAPI عم يرجّع { success, recently_viewed: [...] }
      const list = Array.isArray(data?.recently_viewed) ? data.recently_viewed : [];

      // Normalize products like Trending component
      const norm = list.map((p) => {
        const firstImg = Array.isArray(p.images) && p.images.length ? p.images[0].path : null;
        return {
          id: p.id,
          name: p.name ?? p.title ?? "Product",
          price: Number(p.selling_price ?? p.price ?? p.regular_price ?? 0),
          image_url:
            p.image_url ? p.image_url :
            p.image     ? fileUrl(p.image) :
            firstImg    ? fileUrl(firstImg) :
            PLACEHOLDER
        };
      });

      setProducts(norm);
    } catch (e) {
      setError(e.message || "Failed to load recently viewed products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecentlyViewed();
  }, []);

  const handleAddToCart = async (productId, quantity = 1) => {
    const token = getToken();
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    try {
      setBusyId(productId);
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, quantity }),
      });

      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to add to cart");
      }

      toast.success("Product added to cart");
    } catch (err) {
      toast.error(err.message || "Could not add to cart");
    } finally {
      setBusyId(null);
    }
  };

  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    loadRecentlyViewed();
  };

  // إذا مش عامل لوجين أو ما في منتجات -> ما منعرض السكشن
  if (!getToken() || products.length === 0) {
    return null;
  }

  return (
    <>
      <section className="rv" aria-label="Recently viewed products">
        <div className="rv-head">
          <div className="rv-titles">
            <h2>Recently Viewed</h2>
            <p>Products you've recently viewed</p>
          </div>
          <a className="rv-view" href="/products/recently-viewed">View All</a>
        </div>

        <div className="rv-grid">
          {loading ? (
            Array.from({ length: 4 }, (_, i) => (
              <article className="rv-card skel" key={i}>
                <div className="rv-img"></div>
                <div className="rv-body">
                  <div className="rv-name">\u00A0</div>
                  <div className="rv-price">\u00A0</div>
                  <div className="rv-cart"></div>
                </div>
              </article>
            ))
          ) : error ? (
            <div className="rv-error">
              <p>{error}</p>
            </div>
          ) : (
            products.slice(0, 4).map((product) => (
              <article key={product.id} className="rv-card">
                <div className="rv-img">
                  {product.image_url && (
                    <img 
                      src={product.image_url} 
                      alt={product.name} 
                      loading="lazy" 
                      decoding="async" 
                    />
                  )}
                </div>
                <div className="rv-body">
                  <Link 
                    to={`/product/${product.id}`} 
                    className="rv-name"
                  >
                    {product.name}
                  </Link>
                  <div className="rv-price">{money(product.price)}</div>
                  <button
                    className="rv-cart"
                    aria-label={busyId === product.id ? "Adding..." : "Add to cart"}
                    onClick={() => handleAddToCart(product.id, 1)}
                    disabled={busyId === product.id}
                    title={busyId === product.id ? "Adding..." : "Add to cart"}
                  >
                    {busyId === product.id ? <Loader size={18} /> : <CartIcon />}
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}